import styled from 'styled-components';

import { SANS_FONT_FAMILY } from '@stylesheets/font';

export const Figure = styled.figure`
  position: relative;
  display: inline-block;
  max-width: 100%;
  margin: 0 20px 4px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  /* Collapse the inline-block baseline gap below the image so the overlay's
     0-1 viewBox maps to the image box exactly, not a few px too tall. */
  line-height: 0;
`;

export const Overlay = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export const Scrim = styled.rect`
  fill: ${({ theme }) => theme.colors.scrim};
  fill-opacity: 0.55;
`;

export const Ring = styled.rect`
  fill: none;
  stroke: ${({ theme }) => theme.colors.primary};
  stroke-width: 2.5;
  vector-effect: non-scaling-stroke;
`;

/** Translucent fill of the user-drawn box so the selection reads clearly
 *  against a busy drawing, not just a thin outline. */
export const SelectionFill = styled.rect`
  fill: ${({ theme }) => theme.colors.primary};
  fill-opacity: 0.18;
`;

/** Zoom/pan transform shared by the annotate image and its overlay. */
const zoomTransform = (p: { $s: number; $tx: number; $ty: number }) =>
  `translate(${p.$tx}px, ${p.$ty}px) scale(${p.$s})`;

/** Annotate-mode image: transformed for zoom/pan; a drag draws (no fullscreen). */
export const DrawImage = styled.img<{ $s: number; $tx: number; $ty: number }>`
  display: block;
  width: 100%;
  height: auto;
  user-select: none;
  -webkit-user-drag: none;
  transform-origin: 0 0;
  transform: ${zoomTransform};
`;

/** Overlay carrying the drawn box; transformed identically so it tracks zoom/pan. */
export const AnnotateOverlay = styled.svg<{ $s: number; $tx: number; $ty: number }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  transform-origin: 0 0;
  transform: ${zoomTransform};
`;

/** Transparent, untransformed layer that captures the pointer over the viewport. */
export const CaptureLayer = styled.div<{ $mode: 'draw' | 'pan' }>`
  position: absolute;
  inset: 0;
  touch-action: none;
  cursor: ${({ $mode }) => ($mode === 'pan' ? 'grab' : 'crosshair')};

  &:active {
    cursor: ${({ $mode }) => ($mode === 'pan' ? 'grabbing' : 'crosshair')};
  }
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px 0;
  flex-wrap: wrap;
`;

export const ToolGroup = styled.div<{ $push?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: ${({ $push }) => ($push ? 'auto' : '0')};
  padding: 2px;
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  border-radius: ${({ theme }) => theme.borderRadius.full};
`;

export const ToolButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font: 600 12px/1 ${SANS_FONT_FAMILY};
  cursor: pointer;
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.onSurfaceVariant)};
  background: ${({ theme, $active }) =>
    $active ? `color-mix(in oklch, ${theme.colors.primary} 14%, transparent)` : 'transparent'};
  transition: background 120ms, color 120ms;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceContainerHigh};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const ToolIcon = styled.span.attrs({ className: 'material-symbols-rounded' })`
  font-size: 16px;
  font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  line-height: 1;
`;

export const ZoomLabel = styled.span`
  min-width: 40px;
  text-align: center;
  font: 600 12px/1 ${SANS_FONT_FAMILY};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
`;

export const Hint = styled.figcaption`
  padding: 6px 20px 0;
  font: 400 11.5px/15px ${SANS_FONT_FAMILY};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  display: flex;
  gap: 6px;
  align-items: center;
`;

export const HintButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: ${({ theme }) => theme.colors.primary};

  &:disabled {
    color: ${({ theme }) => theme.colors.onSurfaceVariant};
    cursor: default;
  }
`;

export const Skeleton = styled.div`
  margin: 0 20px 4px;
  height: 160px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surfaceContainerHigh};
`;

export const RegionLabel = styled.figcaption`
  padding: 6px 20px 0;
  font: 400 11.5px/15px ${SANS_FONT_FAMILY};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
`;

export const LoadError = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 20px 4px;
  height: 160px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surfaceContainerHigh};
  font: 400 12px/16px ${SANS_FONT_FAMILY};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
`;
